import { useState } from 'react';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';

export const QuoteForm = ({ isOpen, onClose, onSubmit, configuration, isLoading }) => {
  const [formData, setFormData] = useState({
    customer_name: '',
    customer_email: '',
    customer_phone: '',
    customer_message: ''
  });

  const handleSubmit = (e) => {
    e.preventDefault();
    onSubmit(formData);
  };

  const updateField = (field, value) => {
    setFormData(prev => ({
      ...prev,
      [field]: value
    }));
  };

  return (
    <Dialog open={isOpen} onOpenChange={onClose}>
      <DialogContent className="sm:max-w-[500px] rounded-none" data-testid="quote-form-dialog">
        <DialogHeader>
          <DialogTitle className="text-2xl" style={{ fontFamily: "'Space Grotesk', sans-serif" }}>
            Request a Quote
          </DialogTitle>
          <DialogDescription>
            Fill in your details and we'll send you a detailed quote for your custom steel door.
          </DialogDescription>
        </DialogHeader>
        
        <form onSubmit={handleSubmit} className="space-y-4 py-4">
          {/* Configuration Summary */}
          <div className="bg-secondary p-4 rounded-none space-y-1">
            <h3 className="font-medium text-sm mb-2">Configuration Summary</h3>
            <div className="text-xs space-y-1 font-mono text-muted-foreground">
              <p>Profile: {configuration.profile}</p>
              <p>Type: {configuration.door_type} Door</p>
              <p>Dimensions: {configuration.width}mm × {configuration.height}mm</p>
              <p>Panel: {configuration.panel_style}</p>
              <p>Color: {configuration.color}</p>
              <p>Hardware: {configuration.hardware}</p>
              <p>Glass: {configuration.glass_type}</p>
              {configuration.sidelite_left === 'Yes' && <p>Left Sidelite: Yes</p>}
              {configuration.sidelite_right === 'Yes' && <p>Right Sidelite: Yes</p>}
              {configuration.true_arch_frame === 'Yes' && <p>True Arch Frame: Yes</p>}
            </div>
          </div>

          {/* Customer Details */}
          <div className="space-y-3">
            <div className="space-y-1">
              <Label htmlFor="name">Full Name *</Label>
              <Input
                id="name"
                data-testid="quote-name-input"
                required
                value={formData.customer_name}
                onChange={(e) => updateField('customer_name', e.target.value)}
                className="rounded-none"
                placeholder="John Smith"
              />
            </div>

            <div className="space-y-1">
              <Label htmlFor="email">Email *</Label>
              <Input
                id="email"
                data-testid="quote-email-input"
                type="email"
                required
                value={formData.customer_email}
                onChange={(e) => updateField('customer_email', e.target.value)}
                className="rounded-none"
                placeholder="john@example.com"
              />
            </div>

            <div className="space-y-1">
              <Label htmlFor="phone">Phone Number</Label>
              <Input
                id="phone"
                data-testid="quote-phone-input"
                type="tel"
                value={formData.customer_phone}
                onChange={(e) => updateField('customer_phone', e.target.value)}
                className="rounded-none"
                placeholder="+64 21 123 4567"
              />
            </div>

            <div className="space-y-1">
              <Label htmlFor="message">Additional Notes</Label>
              <Textarea
                id="message"
                data-testid="quote-message-input"
                value={formData.customer_message}
                onChange={(e) => updateField('customer_message', e.target.value)}
                className="rounded-none min-h-[80px]"
                placeholder="Any specific requirements or questions..."
              />
            </div>
          </div>

          <DialogFooter className="gap-2">
            <Button 
              type="button" 
              variant="outline" 
              onClick={onClose}
              className="rounded-none"
              data-testid="quote-cancel-button"
            >
              Cancel
            </Button>
            <Button 
              type="submit" 
              disabled={isLoading}
              className="rounded-none bg-accent hover:bg-accent/90"
              data-testid="quote-submit-button"
            >
              {isLoading ? 'Submitting...' : 'Submit Request'}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
};

export default QuoteForm;
